import React, { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';

// Available languages. To add one: create locales/<code>/*.json and list it here.
export const LANGS = [
  { code: 'it', name: 'Italiano' },
  { code: 'en', name: 'English' },
  { code: 'fr', name: 'Français' },
  { code: 'es', name: 'Español' },
  { code: 'de', name: 'Deutsch' },
  { code: 'pt', name: 'Português' },
];
const CODES = LANGS.map((l) => l.code);
const STORAGE_KEY = 'tnh_lang';

// Loads every locales/<language>/<namespace>.json file and merges them per language (flat, prefixed keys)
const DICT = {};
const ctx = require.context('./locales', true, /\.json$/);
ctx.keys().forEach((k) => {
  const m = k.match(/^\.\/([a-z]{2})\/[^/]+\.json$/);
  if (!m) return;
  const data = ctx(k).default || ctx(k);
  DICT[m[1]] = Object.assign(DICT[m[1]] || {}, data);
});

export function detectLang() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && CODES.includes(saved)) return saved;
  } catch { /* ignore */ }
  if (window.location.hostname.includes('notediassaggio')) return 'it';
  const prefs = navigator.languages || [navigator.language || 'en'];
  for (const p of prefs) {
    const c = String(p).slice(0, 2).toLowerCase();
    if (CODES.includes(c)) return c;
  }
  return 'en';
}

let currentLang = detectLang();
if (typeof document !== 'undefined') document.documentElement.lang = currentLang;

// Current language outside React (PDF requests, Keycloak locale)
export const getLang = () => currentLang;

export function formatDateFor(lang, iso) {
  if (!iso) return '';
  const text = String(iso);
  const date = new Date(/^\d{4}-\d{2}-\d{2}$/.test(text) ? `${text}T00:00:00Z` : text);
  if (Number.isNaN(date.getTime())) return text;
  return new Intl.DateTimeFormat(lang, { dateStyle: 'medium', timeZone: 'UTC' }).format(date);
}

export function formatNumberFor(lang, value, maximumFractionDigits = 1) {
  if (value === null || value === undefined || value === '' || Number.isNaN(Number(value))) return '';
  return new Intl.NumberFormat(lang, { maximumFractionDigits }).format(Number(value));
}

function translate(lang, key, vars) {
  let s = (DICT[lang] && DICT[lang][key]);
  if (s === undefined) s = DICT.en && DICT.en[key];
  if (s === undefined) s = DICT.it && DICT.it[key];
  if (s === undefined) return key;
  if (vars) s = s.replace(/\{(\w+)\}/g, (_, n) => (vars[n] !== undefined ? vars[n] : `{${n}}`));
  return s;
}

const I18nContext = createContext(null);

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(currentLang);

  const setLang = useCallback((l) => {
    if (!CODES.includes(l)) return;
    currentLang = l;
    try { localStorage.setItem(STORAGE_KEY, l); } catch { /* ignore */ }
    document.documentElement.lang = l;
    setLangState(l);
  }, []);

  // The browser tab title follows the language (the site name is a translated text too)
  useEffect(() => { document.title = translate(lang, 'app.site_name'); }, [lang]);

  const value = useMemo(() => {
    const t = (key, vars) => translate(lang, key, vars);
    // Translates a value stored as a stable code (e.g. tc('wine_type', 'red')).
    // If the key does not exist (free text) the value is returned as it is.
    const tc = (prefix, code) => {
      if (code === undefined || code === null || code === '') return '';
      const key = `${prefix}.${code}`;
      const s = translate(lang, key);
      return s === key ? String(code) : s;
    };
    // Dates arrive as 'YYYY-MM-DD' (or ISO timestamps) and are shown in the user's language: '4 Oct 2026', '4 ott 2026'...
    const formatDate = (iso) => formatDateFor(lang, iso);
    // Numbers use the language's decimal separator: 7.2 → '7,2' in Italian. Null/undefined → ''.
    const formatNumber = (value, maximumFractionDigits = 1) => formatNumberFor(lang, value, maximumFractionDigits);
    return { lang, setLang, t, tc, formatDate, formatNumber };
  }, [lang, setLang]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const v = useContext(I18nContext);
  if (!v) throw new Error('useI18n must be used inside I18nProvider');
  return v;
}
